// src/components/serverSetup/ConfirmConfigurationModal.js

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import PropTypes from 'prop-types';

/**
 * Confirmation Modal Component
 * @param {boolean} open - Controls the visibility of the modal.
 * @param {function} onClose - Function to close the modal without saving.
 * @param {object} configData - Data to display in the modal.
 * @param {function} onConfirm - Function to execute on confirmation.
 */
const ConfirmConfigurationModal = ({ open, onClose, configData, onConfirm }) => {
  const {
    selectedGuildName,
    channelName,
    roleConfigurations,
    getRoleNameById,
    getCollectionNameByAddress,
  } = configData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="confirm-configuration-dialog-title"
      aria-describedby="confirm-configuration-dialog-description"
      PaperProps={{
        sx: {
          backgroundColor: '#081529', // Brand background color
          color: '#E0E4ED', // Brand text color
          fontFamily: 'Poppins, sans-serif',
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        id="confirm-configuration-dialog-title"
        sx={{
          backgroundColor: '#040813',
          borderBottom: '1px solid #132A47',
        }}
      >
        <Typography variant="h5" sx={{ color: '#9AC0CD', fontWeight: 600 }}>
          Confirm Configuration
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#0D1F3A' }}>
        <Typography variant="h6" gutterBottom>
          <strong>You are about to save the following configuration for your Discord Server:</strong>
        </Typography>

        {/* Server Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Server:</strong> {selectedGuildName}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Channel:</strong> {channelName}
          </Typography>
        </Box>

        {/* Role Configurations */}
        <Typography variant="h6" gutterBottom>
          Role Configurations:
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: '#132A47', borderRadius: '8px' }}>
          <Table aria-label="role configurations table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>Role Name</TableCell>
                <TableCell sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>Requirements</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roleConfigurations.map((config, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif' }}>
                    {getRoleNameById(config.roleId)}
                  </TableCell>
                  <TableCell sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif' }}>
                    {config.type.charAt(0).toUpperCase() + config.type.slice(1)} {/* Capitalize Type */}
                  </TableCell>
                  <TableCell sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif' }}>
                    {config.type === 'individual' ? (
                      <ul style={{ paddingLeft: '20px', margin: 0 }}>
                        {config.requirements.map((req, reqIndex) => (
                          <li key={reqIndex}>
                            {getCollectionNameByAddress(req.collectionAddress)}: {req.count} NFT(s)
                            {req.traits && req.traits.length > 0 && (
                              <ul style={{ paddingLeft: '20px', margin: '5px 0 0 0' }}>
                                {req.traits.map((trait, traitIndex) => (
                                  <li key={traitIndex}>{trait}</li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Box>
                        <Typography variant="body1">
                          <strong>Combined Collections:</strong>
                        </Typography>
                        <ul style={{ paddingLeft: '20px', margin: '5px 0' }}>
                          {config.combinedRequirement.collectionAddresses.map((addr, addrIndex) => (
                            <li key={addrIndex}>{getCollectionNameByAddress(addr)}</li>
                          ))}
                        </ul>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          <strong>Combined NFT Count:</strong> {config.combinedRequirement.combinedCount}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: '#040813', borderTop: '1px solid #132A47' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontFamily: 'Poppins, sans-serif',
            color: '#A1D1F0',
            borderColor: '#A1D1F0',
            '&:hover': {
              borderColor: '#9AC0CD',
              backgroundColor: 'rgba(154, 192, 205, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontFamily: 'Poppins, sans-serif',
            backgroundColor: '#728C63',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#8BA675',
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConfirmConfigurationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  configData: PropTypes.shape({
    selectedGuildName: PropTypes.string.isRequired,
    channelName: PropTypes.string.isRequired,
    roleConfigurations: PropTypes.arrayOf(
      PropTypes.shape({
        roleId: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired, // 'individual' or 'combined'
        requirements: PropTypes.arrayOf(
          PropTypes.shape({
            collectionAddress: PropTypes.string.isRequired,
            count: PropTypes.number.isRequired,
            traits: PropTypes.arrayOf(PropTypes.string),
          })
        ),
        combinedRequirement: PropTypes.shape({
          collectionAddresses: PropTypes.arrayOf(PropTypes.string).isRequired,
          combinedCount: PropTypes.number.isRequired,
        }),
      })
    ).isRequired,
    getRoleNameById: PropTypes.func.isRequired,
    getCollectionNameByAddress: PropTypes.func.isRequired,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default ConfirmConfigurationModal;

